import { Box, useMediaQuery, useTheme } from '@mui/material';
import MainLayout from 'src/pages/private';
import { useState, FC } from 'react';
import TextField from 'src/components/InputFields/TextField';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox2';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { useFormik } from 'formik';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { discountInitialValues } from 'src/utils/initialValues.utils';
import { apiUrls } from 'src/utils/properties.utils';
import { discountValidationSchema } from 'src/utils/validationScheme.utils';
import DiscountGrid from 'src/components/Grids/DiscountGrid';
import TextArea from 'src/components/InputFields/TextArea';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import SearchBar from 'src/components/InputFields/Searchbar';
import {
  PrimaryActionButton,
  ButtonIcon,
  OverflowContainer,
} from 'src/components/StyledComponents/StyledPageComponents';
import { calculateDialogHeightByErrorCount } from 'src/utils/dialog.utils';

const DiscountManagement: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [discountAdded, setDiscountAdded] = useState(false);
  const { showSnackbar } = StyledSnackbar();
  const [searchTerm, setSearchTerm] = useState('');

  const formik = useFormik({
    initialValues: discountInitialValues,
    validationSchema: discountValidationSchema,
    onSubmit: (values) => handleSubmit(values),
  });

  const getDialogHeight = () =>
    calculateDialogHeightByErrorCount(formik, 700, 30);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const addDiscount = await axiosTokenInstance.post(apiUrls.ADD_DISCOUNT, {
        name: values.name,
        description: values.description,
        percentage: values.percentage || null,
        defaultValue: values.defaultValue || null,
      });

      showSnackbar(addDiscount.data.message, 'success');
      setDiscountAdded((prev) => !prev);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  return (
    <MainLayout
      title="Discount Management"
      searchBar={
        <SearchBar
          id="search"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search discounts by name"
        />
      }
      button={
        <PrimaryActionButton
          className="btn-primary"
          width="150px"
          onClick={handleClickOpen}
        >
          <ButtonIcon component="i" className="bx bx-plus 1" />
          New Discount
        </PrimaryActionButton>
      }
    >
      <DiscountGrid state={discountAdded} searchTerm={searchTerm} />
      <Box display="flex">
        <OverflowContainer display="flex" flexDirection="column">
          <CustomDialog
            height={`${getDialogHeight()}px`}
            open={open}
            handleClose={handleClose}
            title="Add New Discount"
            subtitle="Fill in the details below to add a new discount to the system."
            onSubmit={formik.handleSubmit}
            smScreen={smScreen}
            buttonDisabled={loading}
          >
            <TextField
              width="480px"
              id="name"
              label="Discount Name"
              formikProps={formik}
            />

            <TextArea
              id="description"
              label="Description"
              formikProps={formik}
            />

            <TextField
              width="480px"
              id="percentage"
              label="Percentage"
              formikProps={formik}
            />
            <TextField
              width="480px"
              id="defaultValue"
              label="Default Value"
              formikProps={formik}
            />
          </CustomDialog>
        </OverflowContainer>
      </Box>
    </MainLayout>
  );
};

export default DiscountManagement;
